/* LT Forged v7 — configurator.
 *
 * FCPICK (2026-06-16): Step 02 is now a config-driven FINISH × COLOR picker —
 *   8 finish cards (LTF_FINISHES) then 49 color chips (LTF_COLORS), full
 *   cross-product (every color on every finish, Michael-confirmed). state.finish
 *   = { finishId, finish, color, colorHex, name }. Cart props 'Face color' /
 *   '_Face finish tier' REPLACED by explicit 'Finish' + 'Color' (+ '_Color hex').
 *   Review adds a Color row. Edit the two arrays near the top to add/remove
 *   a finish or color — UI, Review and cart props all regenerate. Selection UI
 *   only; per-finish render images attach in the later 3D phase (see hook).
 *
 * Four fixes vs v6:
 *   1. Trim/variant 4th dropdown (NEUTRAL labels — engine/drivetrain or
 *      generic Standard/Sport/Performance + OE wheel diameter). Drives
 *      stock fitment auto-fill and the brake-clearance note. NEVER
 *      surfaces banned marketing trim names (Blackwing / GT3 / Z06 /
 *      Hellcat / R34 / Dark Horse / ZR1 / Demon / TRX).
 *   2. Face step REMOVED — face is set by the product page (per-wheel
 *      binding). Steps: Car → Finish → Cap → Fitment → Review (5).
 *   3. Financing strip + per-mo estimate (Affirm 24-mo 0% APR fallback
 *      per .auto-memory/reference_lt_payment_and_financing.md).
 *   4. Review's confirm-build gates RESTORED to the full v2/older set:
 *      4 checkboxes (vehicle / fitment / terms+lead-time / 48-hr
 *      named-human). Lock CTA stays DISABLED until all 4 are ticked.
 *
 * Pass 1 additions (2026-06-02):
 *   - Cap step: color swatch wiring (capColor / capColorHex state)
 *   - Cap color written to cart payload
 *
 * Pass 2 additions (2026-06-02):
 *   - Wheel-Size proxy URL infrastructure (2A)
 *   - Step transition opacity fade (2E)
 *   - Loading state during catalog fetch (2E)
 *
 * Pass 3 fixes (adversarial review 2026-06-03):
 *   - Lead-time cart property: '8-12 weeks' → '8–12 weeks' (en-dash)
 *   - Finish swatches: aria-label + aria-pressed added for screen readers
 *   - Cap tiles: aria-pressed added
 *   - stepSatisfied() failure now surfaces an inline hint to user
 *   - updateNav() adds/removes .lt7-cfg__nav-hint when step is blocked
 *
 * Fix-pass R1 (2026-06-03):
 *   - Step 4 renamed from "Size/Sizing" to "Fitment" (label + name + CTA)
 *
 * Fix-pass R5 (2026-06-03):
 *   - Part A: trim label cleanup — FILLER_TRIM_LEVELS set filters localized
 *     base words (e.g. "Basis" / German for Base) from trim_levels prefix
 *     so Audi A6 shows "45 TFSi quattro" not "Basis · 45 TFSi quattro".
 *   - Part B: VIN entry mode — two-mode toggle (Picker / Enter VIN).
 *     VIN decoded via NHTSA vPIC API (client-side, free, no key, CORS-ok).
 *     Pre-fills Y/M/Mo/Trim and auto-loads fitment. Falls back to concierge
 *     email path on decode failure or unknown make/model.
 *
 * Fix-pass R6 (2026-06-04):
 *   - Bug 1: VIN model matching hardened. NHTSA returns "CT5" but WS has
 *     "CT5-V"; NHTSA returns "Q6" but WS only has "Q6 e-tron". New
 *     matchWsModel() uses exact + prefix + Trim-word scoring to select the
 *     right WS model — never auto-selects a wrong model.
 *     Also: status line prefixed "VIN read: ..." after decode; vinDecoded
 *     state cleared on manual picker change.
 *   - Bug 2: Spacing/visibility — CSS added for reach-hint links and
 *     concierge block; reach-hint link text shortened to reduce repetition.
 *
 * Round 7 (2026-06-04):
 *   Part 1 — WS schema audit: confirmed bolt_pattern, centre_bore, pcd,
 *     wheel_fasteners (thread_size, type), wheel_tightening_torque ARE
 *     present in the `technical` block of search/by_model/ responses.
 *     Website display gap ≠ API gap. Brake caliper clearance: not in any
 *     OE fitment DB — customer measurement / concierge covers it.
 *   Part 2 — Full factory spec captured: loadLiveFitment() now stores the
 *     complete `technical` block in state.oeTechnical. submitToCart()
 *     writes every factory-ready field (bolt pattern, centre bore, lug spec,
 *     OE torque, chosen front/rear wheel spec, staggered flag, construction,
 *     set qty) as Shopify line-item properties.
 *   Part 3 — Trim-confirm polish: VIN decode pre-selects the best-guess
 *     trim but does NOT auto-load fitment. Instead, a teal callout appears:
 *     "Confirm your trim to load exact fitment." Fitment loads only on
 *     explicit confirm (button click or manual trim re-selection). Manual
 *     picker path unchanged — trim change = implicit confirm.
 *
 * Spec-polish (2026-06-05):
 *   - 'Face finish' renamed → 'Face color' (customer-facing; same data).
 *   - 'Face finish tier' renamed → '_Face finish tier' (admin-only).
 *   - OE technical fields prefixed with '_' ('_OE bolt pattern',
 *     '_OE center bore', '_OE lug spec') — hidden from customer order
 *     email, visible to admin + factory.
 *
 * v6 wins inherited: body scroll lock, fixed-position section, hide-
 * siblings, brand tokens, per-wheel binding, sizing rework, concierge.
 */
(function () {
  'use strict';

  // ==========================================================================
  // Wheel-Size API proxy — set this to your Cloudflare Worker URL before launch
  // KEY ROTATED — was exposed pre-launch. Now proxied via Cloudflare Worker. See deploy-runbook.md
  // ==========================================================================
  const LT_WS_PROXY_URL = 'https://lt-forged-ws-proxy.ltforgedwheels.workers.dev/ws-proxy';
  // Usage: fetch(LT_WS_PROXY_URL + '/makes/?year=' + year)
  // The Worker injects the Wheel-Size user_key server-side — never put the key in this file.
  const WS_ENABLED = /^https?:\/\//.test(LT_WS_PROXY_URL) && !/REPLACE_WITH/.test(LT_WS_PROXY_URL);

  // Fetch a Wheel-Size endpoint via the proxy. Returns the `data` array, or null on any
  // failure so the caller falls back to the local catalog — live data never hard-breaks the UI.
  async function wsGet(path) {
    if (!WS_ENABLED) return null;
    try {
      const r = await fetch(LT_WS_PROXY_URL + path, { headers: { Accept: 'application/json' } });
      if (!r.ok) return null;
      const j = await r.json();
      return (j && Array.isArray(j.data)) ? j.data : null;
    } catch (_) { return null; }
  }
  function wsEsc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Localized "base/standard" words that Wheel-Size uses in trim_levels but which add
  // no meaningful info for US customers. "Basis" = German for Base (seen on Audi A6 from WS API).
  // Filtering these prevents labels like "Basis · 45 TFSi quattro" on US-facing trims.
  const FILLER_TRIM_LEVELS = new Set([
    'basis','base','std','standard','ausführung','standaard','normal','basic','entry',
  ]);

  const STEPS = [
    { id: 'car',    label: '01 Car',    name: 'Your car',     ctaNext: 'Next: finish' },
    { id: 'finish', label: '02 Finish', name: 'Finish',       ctaNext: 'Continue to cap' },
    { id: 'cap',    label: '03 Cap',    name: 'Cap',          ctaNext: 'Continue to fitment' },
    { id: 'sizing', label: '04 FIT',    name: 'Fitment',      ctaNext: 'Continue to review' },
    { id: 'review', label: '05 Review', name: 'Review',         ctaNext: '' },
  ];

  // ════════════════════════════════════════════════════════════════════════
  //  FINISH × COLOR PICKER — CONFIG (Step 02). EDIT HERE.
  //  Brandon: add/remove a finish or colour by editing ONE line in these two
  //  arrays — the Step-02 UI (finish cards + colour grid), the Review summary
  //  and the cart line-item props all regenerate from these. No HTML edits.
  //
  //  Model (LOCKED 2026-06-16): customer picks a FINISH first, then any COLOUR.
  //  Every colour is available on every finish (Michael confirmed) — full
  //  cross-product, no availability matrix.
  //
  //  Finish list is PROVISIONAL pending the supplier — see NEEDS-CONFIRM.md.
  //  To add a finish:  add { id, name, flag, desc } below.
  //  To add a colour:  add { name, hex } to LTF_COLORS below.
  //  Colour hex are screen samples of Michael's chart caps; the factory ROC
  //  paint-card number remains the source of truth for production colour.
  // ════════════════════════════════════════════════════════════════════════
  const LTF_FINISHES = [
    { id: 'gloss',    name: 'Gloss',                flag: 'GL', desc: 'High-shine, mirror-smooth' },
    { id: 'satin',    name: 'Satin',                flag: 'SA', desc: 'Soft sheen, low glare' },
    { id: 'matte',    name: 'Matte',                flag: 'MA', desc: 'Flat, zero reflection' },
    { id: 'brushed',  name: 'Brushed',              flag: 'BR', desc: 'Directional metal grain' },
    { id: 'polished', name: 'Polished',             flag: 'PO', desc: 'Liquid-metal shine' },
    { id: 'chrome',   name: 'Chrome',               flag: 'CH', desc: 'Full chrome-plated mirror' },
    { id: 'candy',    name: 'Translucent / Candy',  flag: 'TC', desc: 'Candy tint over metal' },
    { id: 'machined', name: 'Silver (machined)',    flag: 'SM', desc: 'Machined silver face' },
  ];
  // 49 colours sampled from Michael's swatch chart (2026-06-16). name + screen hex.
  const LTF_COLORS = [
    { name: 'Flamingo Pink',         hex: '#F652C2' },
    { name: 'Light Red',             hex: '#FB394A' },
    { name: 'Scarlet Red',           hex: '#FB394F' },
    { name: 'Fire Red',              hex: '#B63642' },
    { name: 'Merlot',                hex: '#753944' },
    { name: 'Amber Tone',            hex: '#DC703F' },
    { name: 'Butterscotch',          hex: '#DC936B' },
    { name: 'Copper Penny',          hex: '#FD9E67' },
    { name: 'Bronze Brickell',       hex: '#ECB181' },
    { name: 'Champagne',             hex: '#E0BBA8' },
    { name: 'Imperial Gold',         hex: '#FED99D' },
    { name: 'Honeycomb',             hex: '#F3DFA5' },
    { name: 'Patina Gold',           hex: '#CFB896' },
    { name: 'Satin Gold',            hex: '#C19F75' },
    { name: 'Gloss Gold',            hex: '#B79156' },
    { name: 'Heritage Gold',         hex: '#BC9B62' },
    { name: 'Denim Blue',            hex: '#4F5870' },
    { name: 'Deco Teal',             hex: '#0AEAE2' },
    { name: 'Fountain Blue',         hex: '#5EC4E4' },
    { name: 'Biscayne Blue',         hex: '#05C1F0' },
    { name: 'Vintage Rose',          hex: '#E3DAD5' },
    { name: 'Matte Clear',           hex: '#C4C3C3' },
    { name: 'Gloss Clear',           hex: '#D6D8D7' },
    { name: 'Light Smoke',           hex: '#CAC7CB' },
    { name: 'Space Grey',            hex: '#C0B9AE' },
    { name: 'Platinum',              hex: '#B5A79F' },
    { name: 'Dark Smoke',            hex: '#8F8683' },
    { name: 'Stealth Grey',          hex: '#797974' },
    { name: 'Midnight Smoke',        hex: '#686665' },
    { name: 'Miami White',           hex: '#E3E3E1' },
    { name: 'Battleship Grey',       hex: '#A1A7AD' },
    { name: 'Pewter',                hex: '#9F9987' },
    { name: 'Satin Silver',          hex: '#AEB3B4' },
    { name: 'Gloss Silver',          hex: '#DFE0E2' },
    { name: 'Textured Gunmetal',     hex: '#A29DA2' },
    { name: 'Matte Gunmetal',        hex: '#C5C6C7' },
    { name: 'Gloss Gunmetal',        hex: '#B4B8BB' },
    { name: 'Gloss Charcoal',        hex: '#8B8B8A' },
    { name: 'Textured Bronze',       hex: '#988778' },
    { name: 'Satin Bronze',          hex: '#544938' },
    { name: 'Gloss Bronze',          hex: '#3D2812' },
    { name: 'Espresso',              hex: '#3D2A26' },
    { name: 'Gloss Black',           hex: '#282A2F' },
    { name: 'Satin Black',           hex: '#383734' },
    { name: 'Textured Black',        hex: '#35312D' },
    { name: 'Matte Black',           hex: '#29282A' },
    { name: 'Frozen Purple',         hex: '#8D61E3' },
    { name: 'Brushed Purple',        hex: '#7E62B5' },
    { name: 'Frozen Stone Purple',   hex: '#71569A' },
  ];
  // Default selection (step pre-satisfied): Gloss + Gloss Black.
  const LTF_DEFAULT_FINISH = 'gloss';
  const LTF_DEFAULT_COLOR  = 'Gloss Black';

  const GATE_KEYS = ['vehicle', 'fitment', 'terms', 'named'];

  // Approved makes shown in the picker. Anything not on this list routes to the request path.
  // Case-insensitive match against API name/slug. Edit this array to add/remove makes.
  const MAKE_ALLOWLIST = [
    'Acura','Alfa Romeo','Aston Martin','Audi','Bentley','BMW','Buick','Bugatti',
    'Cadillac','Chevrolet','Chrysler','Dodge','Ferrari','Ford','Genesis','GMC',
    'Honda','Hyundai','Infiniti','Jaguar','Jeep','Kia','Lamborghini','Land Rover',
    'Lexus','Lincoln','Lotus','Lucid','Maserati','Mazda','McLaren','Mercedes-Benz',
    'Mitsubishi','Nissan','Porsche','RAM','Rivian','Rolls-Royce','Rolls Royce',
    'Subaru','Tesla','Toyota','Volkswagen','Volvo',
  ];
  // Canonical lower-case set for fast lookup
  const _ALLOWLIST_LC = new Set(MAKE_ALLOWLIST.map(s => s.toLowerCase()));
  function makeAllowed(name) {
    const n = (name || '').toLowerCase().trim();
    return _ALLOWLIST_LC.has(n);
  }

  // Hint copy shown below the disabled Next button when the current step isn't satisfied.
  const STEP_HINT = [
    'Select a year, make, model and trim to continue.',
    'Pick a finish to continue.',
    'Choose a cap style to continue.',
    'Enter your fitment to continue.',
    'Tick all four boxes to lock your build.',
  ];

  const FALLBACK_CATALOG = {
    makes: {
      'BMW':           ['3 Series','M3','M4','M5','5 Series','X5'],
      'Cadillac':      ['CT4','CT5','Escalade'],
      'Chevrolet':     ['Camaro','Corvette','Silverado'],
      'Ford':          ['F-150','Mustang','Bronco'],
      'Porsche':       ['718 Cayman','911','Cayenne','Macan','Taycan'],
      'Tesla':         ['Model 3','Model S','Model X','Model Y'],
      'Toyota':        ['GR Corolla','GR86','GR Supra','Tacoma','Tundra'],
    },
    trims: {},
    default_fitment: {},
  };

  /* =====================================================================
   * Layout / scroll lock — inherited from v6 (verbatim semantics).
   * ===================================================================== */
  function measureHeader() {
    const cands = [
      'header.section-header',
      '.shopify-section-group-header-group',
      'header[role="banner"]',
      'header.header',
      'header',
    ];
    for (const sel of cands) {
      const el = document.querySelector(sel);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (rect.height > 0) return Math.round(rect.height);
    }
    return 64;
  }

  function applyLayoutMetrics(root) {
    const h = measureHeader();
    root.style.setProperty('--lt7-header-h', h + 'px');
  }

  function lockBodyScroll() {
    document.documentElement.classList.add('lt7-cfg-active');
    document.body.classList.add('lt7-cfg-active');
  }
  function unlockBodyScroll() {
    document.documentElement.classList.remove('lt7-cfg-active');
    document.body.classList.remove('lt7-cfg-active');
  }

  /* =====================================================================
   * Configurator lifecycle.
   * ===================================================================== */
  function initConfigurator() {
    const root = document.querySelector('[data-lt7-cfg]');
    if (!root) return;

    const variantId = root.getAttribute('data-variant-id') || '';
    const productHandle = root.getAttribute('data-product-handle') || '';
    const productTitle = root.getAttribute('data-product-title') || 'LT Forged wheel';
    const heroImg = root.getAttribute('data-hero-image') || '';
    const placeholderImg = root.getAttribute('data-placeholder-image') || '';
    const hasOwnRender = root.getAttribute('data-has-own-render') === 'true';
    const productFace = root.getAttribute('data-product-face') || 'LTM-01';
    const perSetMoney = root.getAttribute('data-per-set-money') || '$4,200';
    const perSetCents = parseInt(root.getAttribute('data-per-set-cents') || '420000', 10);
    const perMoMoneyLiquid = root.getAttribute('data-per-mo-money') || '';
    const catalogUrl = root.getAttribute('data-catalog-url') || '';

    let CATALOG = FALLBACK_CATALOG;

    const state = {
      step: 0,
      year: '',
      make: '',
      makeSlug: '',
      model: '',
      modelSlug: '',
      trim: '',
      trimIdx: -1,
      liveMakes: null,
      liveModels: null,
      liveMods: null,
      face: productFace,
      finish: (function () {
        // FINISH×COLOUR default — Gloss + Gloss Black (see LTF_DEFAULT_* config).
        const f = LTF_FINISHES.find(x => x.id === LTF_DEFAULT_FINISH) || LTF_FINISHES[0];
        const c = LTF_COLORS.find(x => x.name === LTF_DEFAULT_COLOR) || LTF_COLORS[0];
        return { finishId: f.id, finish: f.name, color: c.name, colorHex: c.hex, name: f.name + ' · ' + c.name };
      })(),
      cap: 'Stock · cyan + white',
      capColor: 'Teal (House)',
      capColorHex: '#4DB8D8',
      sizingMode: 'stock',
      stockFit: null,
      brakeNote: '',
      customFit: { fd:'', fw:'', fo:'', ft:'', rd:'', rw:'', ro:'', rt:'' },
      gates: { vehicle: false, fitment: false, terms: false, named: false },
      carInputMode: 'picker', // 'picker' | 'vin'
      vinDecoded: false,       // true after NHTSA decode; cleared on manual picker change
      oeTechnical: null,       // { boltPattern, centreBore, pcd, studHoles, lugType, lugThread, torque } from WS technical block
      trimPendingConfirm: false, // VIN pre-selected trim — waiting for explicit confirm before loading fitment
    };

    const $  = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));

    const elCanvasImg   = $('[data-lt7-cfg-canvas-image]');
    const elCanvasZone  = $('[data-lt7-cfg-canvas-zone]');
    const elCanvasNote  = $('[data-lt7-cfg-canvas-note]');
    const elLayout      = $('.lt7-cfg__layout'); // FITSTEP: Step-4 stage-takeover target
    const elSteps       = $$('[data-lt7-cfg-step]');
    const elProgress    = $$('[data-lt7-cfg-progress-step]');
    const elStepNum     = $('[data-lt7-cfg-step-num]');
    const elStepName    = $('[data-lt7-cfg-step-name]');
    const elNavBack     = $('[data-lt7-cfg-back]');
    const elNavNext     = $('[data-lt7-cfg-next]');
    const elNavLock     = $('[data-lt7-cfg-lock]');
    const elNavMeta     = $('[data-lt7-cfg-navbar-meta]');

    const elYear  = $('[data-lt7-cfg-year]');
    const elMake  = $('[data-lt7-cfg-make]');
    const elModel = $('[data-lt7-cfg-model]');
    const elTrim  = $('[data-lt7-cfg-trim]');
    const elYmmStatus   = $('[data-lt7-cfg-ymm-status]');
    const elYmmBrake    = $('[data-lt7-cfg-ymm-brake]');
    const elModelReach  = $('[data-lt7-cfg-model-reach]');
    const elTrimReach   = $('[data-lt7-cfg-trim-reach]');

    const elSizingStockHost = $('[data-lt7-cfg-sizing-stock]');
    const elSizingCustom = $('[data-lt7-cfg-sizing-custom]');
    const elSizingModeStock = $('[data-lt7-cfg-sizing-mode-stock]');
    const elSizingModeCustom = $('[data-lt7-cfg-sizing-mode-custom]');

    // VIN mode refs
    const elCarModeBtns  = $$('[data-lt7-cfg-car-mode]');
    const elCarPanels    = $$('[data-lt7-cfg-car-panel]');
    const elVinInput     = $('[data-lt7-cfg-vin-input]');
    const elVinDecode    = $('[data-lt7-cfg-vin-decode]');
    const elVinStatus    = $('[data-lt7-cfg-vin-panel-status]');

    const elReviewVehicle = $('[data-lt7-cfg-review-vehicle]');
    const elReviewTrim    = $('[data-lt7-cfg-review-trim]');
    const elReviewFace    = $('[data-lt7-cfg-review-face]');
    const elReviewFinish  = $('[data-lt7-cfg-review-finish]');
    const elReviewColor   = $('[data-lt7-cfg-review-color]');
    const elReviewCap     = $('[data-lt7-cfg-review-cap]');
    const elReviewSizing  = $('[data-lt7-cfg-review-sizing]');
    const elReviewFitcheck = $('[data-lt7-cfg-review-fitcheck]');
    const elReviewPerMo   = $('[data-lt7-cfg-review-permo]');
    const elFinstripPerMo = $('[data-lt7-cfg-finstrip-permo]');

    /* ----- nav hint (inline validation feedback) ----- */
    let elNavHint = $('[data-lt7-cfg-nav-hint]');
    if (!elNavHint) {
      elNavHint = document.createElement('p');
      elNavHint.setAttribute('data-lt7-cfg-nav-hint', '');
      elNavHint.setAttribute('role', 'alert');
      elNavHint.setAttribute('aria-live', 'polite');
      elNavHint.className = 'lt7-cfg__nav-hint';
      elNavHint.setAttribute('hidden', '');
      const navbar = $('[data-lt7-cfg-navbar]');
      if (navbar) navbar.insertBefore(elNavHint, navbar.firstChild);
    }

    function showNavHint(text) {
      if (!elNavHint) return;
      elNavHint.textContent = text;
      elNavHint.removeAttribute('hidden');
    }
    function hideNavHint() {
      if (!elNavHint) return;
      elNavHint.setAttribute('hidden', '');
      elNavHint.textContent = '';
    }

    /* ----- YMM + Trim population ----- */
    function populateYears() {
      const now = new Date().getFullYear();
      const start = 1990; // P1-29 FIXPASS: was 2010 — excluded hero cars like the 1999 R34. Pre-1990 → concierge line in step 1.
      const end = now + 1;
      const opts = ['<option value="">Year</option>'];
      for (let y = end; y >= start; y--) opts.push(`<option value="${y}">${y}</option>`);
      elYear.innerHTML = opts.join('');
    }
    async function populateMakes() {
      const opts = ['<option value="">Make</option>'];
      if (WS_ENABLED && !state.year) {
        elMake.innerHTML = '<option value="">Select a year first</option>';
        elMake.disabled = true;
        state.liveMakes = null;
        return;
      }
      let live = null;
      if (WS_ENABLED && state.year) {
        const d = await wsGet(`/makes/?year=${encodeURIComponent(state.year)}`);
        if (d && d.length) live = d;
      }
      state.liveMakes = live;
      if (live) {
        // Filter to approved allowlist; route everything else to a request path
        const allowed = live.filter(m => makeAllowed(m.name));
        allowed.forEach(m => opts.push(`<option value="${wsEsc(m.slug)}">${wsEsc(m.name)}</option>`));
        opts.push('<option value="__request__" class="lt7-cfg__make-request">Don’t see your make? Request it →</option>');
        elMake.innerHTML = opts.join('');
        elMake.disabled = false;
        return;
      }
      // Fallback: local catalog (WS disabled, or live fetch failed)
      Object.keys(CATALOG.makes || {}).sort()
        .filter(m => makeAllowed(m))
        .forEach(m => opts.push(`<option value="${wsEsc(m)}">${wsEsc(m)}</option>`));
      opts.push('<option value="__request__">Don’t see your make? Request it →</option>');
      elMake.innerHTML = opts.join('');
      elMake.disabled = false;
    }
    async function populateModels() {
      const opts = ['<option value="">Model</option>'];
      let live = null;
      if (WS_ENABLED && state.liveMakes && state.makeSlug && state.year) {
        const d = await wsGet(`/models/?make=${encodeURIComponent(state.makeSlug)}&year=${encodeURIComponent(state.year)}&region=usdm`);
        if (d && d.length) live = d;
      }
      state.liveModels = live;
      if (live) {
        live.forEach(m => opts.push(`<option value="${wsEsc(m.slug)}">${wsEsc(m.name)}</option>`));
        elModel.innerHTML = opts.join('');
        elModel.disabled = false;
        return;
      }
      // Fallback: local catalog keyed by make display name
      const models = (CATALOG.makes && CATALOG.makes[state.make]) || [];
      models.forEach(mo => opts.push(`<option value="${wsEsc(mo)}">${wsEsc(mo)}</option>`));
      elModel.innerHTML = opts.join('');
      elModel.disabled = !state.make;
    }

    function lookupTrims(model) {
      if (!model) return [];
      const direct = (CATALOG.trims || {})[model];
      if (Array.isArray(direct) && direct.length) return direct;
      const def = (CATALOG.default_fitment || {})[model];
      if (def && def.fd) {
        return [{ label: `Standard · ${def.fd}″`, fit: def }];
      }
      return [{ label: 'Standard — concierge will confirm', fit: null }];
    }

    // Format a bare engine-code trim value ("3.0TT" → "3.0L TT", "6.2SC" → "6.2L SC").
    function fmtEng(raw) {
      const m = /^(\d+\.\d+)([A-Za-z]*)$/.exec(raw);
      if (!m) return raw;
      const disp = parseFloat(m[1]).toFixed(1) + 'L';
      const suf = m[2].toUpperCase();
      return suf ? `${disp} ${suf}` : disp;
    }

    // Build a clean human trim label from a Wheel-Size modification object.
    // Priority: named OEM level (trim_levels e.g. BLACKWING) > OEM name (m.trim) > engine code.
    // Engine codes (digit.digit[alpha], no spaces) are reformatted as "X.XL YY".
    // Filler trim_levels words (localized base names like "Basis") are skipped.
    function buildTrimLabel(mod) {
      const raw = (mod.trim || mod.name || mod.slug || '').trim();
      const levels = mod.trim_levels || [];
      const namedLevel = levels.find(l => l.length >= 4 && /^[A-Za-z]+$/.test(l) && !FILLER_TRIM_LEVELS.has(l.toLowerCase()));
      const isEngCode = /^\d+\.\d+[A-Za-z]*$/.test(raw);
      if (namedLevel) {
        const title = namedLevel.charAt(0).toUpperCase() + namedLevel.slice(1).toLowerCase();
        if (isEngCode) return `${title} · ${fmtEng(raw)}`;
        if (!raw.toLowerCase().includes(namedLevel.toLowerCase())) return `${title} · ${raw}`;
        return raw;
      }
      return isEngCode ? fmtEng(raw) : raw;
    }

    async function populateTrims() {
      const opts = ['<option value="">Trim</option>'];
      let live = null;
      if (WS_ENABLED && state.liveModels && state.makeSlug && state.modelSlug && state.year) {
        const d = await wsGet(`/modifications/?make=${encodeURIComponent(state.makeSlug)}&model=${encodeURIComponent(state.modelSlug)}&year=${encodeURIComponent(state.year)}&region=usdm`);
        if (d && d.length) {
          // Build labels, then deduplicate in two passes:
          // Pass 1 — collapse entries that render identical labels (generation overlap for a given year).
          // Pass 2 — drop bare engine-only labels when a "Named · Engine" label for the same engine
          //           already exists (Wheel-Size inconsistently omits trim_levels on facelift entries).
          const mapped = d.map(m => ({ slug: m.slug, label: buildTrimLabel(m) }));
          const seen = new Set();
          const deduped = mapped.filter(t => { if (seen.has(t.label)) return false; seen.add(t.label); return true; });
          const namedEngs = new Set(
            deduped.filter(t => t.label.includes(' · ')).map(t => t.label.split(' · ').slice(1).join(' · '))
          );
          live = namedEngs.size ? deduped.filter(t => !namedEngs.has(t.label)) : deduped;
        }
      }
      state.liveMods = live;
      if (live) {
        live.forEach((t, idx) => opts.push(`<option value="${idx}">${wsEsc(t.label)}</option>`));
        elTrim.innerHTML = opts.join('');
        elTrim.disabled = false;
      } else {
        const trims = lookupTrims(state.model);
        trims.forEach((t, idx) => {
          const lbl = (t && t.label) ? t.label : `Trim ${idx + 1}`;
          opts.push(`<option value="${idx}">${wsEsc(lbl)}</option>`);
        });
        elTrim.innerHTML = opts.join('');
        elTrim.disabled = !state.model || trims.length === 0;
      }
      state.trim = '';
      state.trimIdx = -1;
      state.brakeNote = '';
      hideBrakeNote();
    }

    // Live fitment for the selected modification → maps Wheel-Size wheels[] into the stockFit shape.
    // R7: also captures the technical block (bolt pattern, centre bore, lug spec) confirmed present in API.
    async function loadLiveFitment() {
      state.stockFit = null;
      state.brakeNote = '';
      state.oeTechnical = null;
      const mod = state.liveMods && state.liveMods[state.trimIdx];
      if (!mod) return;
      const d = await wsGet(`/search/by_model/?make=${encodeURIComponent(state.makeSlug)}&model=${encodeURIComponent(state.modelSlug)}&year=${encodeURIComponent(state.year)}&modification=${encodeURIComponent(mod.slug)}`);
      if (!d || !d.length) return;
      const rec = d[0];
      // Capture OE technical data — bolt_pattern, centre_bore, lug spec confirmed populated in WS API (R7 audit)
      const tech = rec.technical;
      if (tech) {
        const lf = tech.wheel_fasteners || {};
        state.oeTechnical = {
          boltPattern: tech.bolt_pattern            || '',
          centreBore:  tech.centre_bore             || '',
          pcd:         tech.pcd != null ? String(tech.pcd) : '',
          studHoles:   tech.stud_holes != null ? String(tech.stud_holes) : '',
          lugType:     lf.type                      || '',
          lugThread:   lf.thread_size               || '',
          torque:      tech.wheel_tightening_torque || '',
        };
      }
      const wheels = rec.wheels || [];
      const w = wheels.find(x => x.is_stock) || wheels[0];
      if (!w) return;
      const f = w.front || {};
      const rear = (w.rear && w.rear.rim_diameter != null) ? w.rear : f; // empty rear = square setup
      const fmt = (n) => (n == null ? '' : String(n).replace(/\.0$/, ''));
      state.stockFit = {
        fd: fmt(f.rim_diameter), fw: fmt(f.rim_width), fo: fmt(f.rim_offset), ft: f.tire || '',
        rd: fmt(rear.rim_diameter), rw: fmt(rear.rim_width), ro: fmt(rear.rim_offset), rt: rear.tire || '',
        isStaggered: !!(w.rear && w.rear.rim_diameter != null &&
          (w.rear.rim_diameter !== f.rim_diameter || w.rear.rim_width !== f.rim_width)),
        isRunflat: !!w.is_runflat_tires,
      };
    }

    function hideBrakeNote() {
      if (!elYmmBrake) return;
      elYmmBrake.textContent = '';
      elYmmBrake.setAttribute('hidden', '');
    }
    function showBrakeNote(note) {
      if (!elYmmBrake) return;
      if (!note) { hideBrakeNote(); return; }
      elYmmBrake.textContent = note;
      elYmmBrake.removeAttribute('hidden');
    }

    function updateYmmStatus() {
      const ok = state.year && state.make && state.model && state.trim;
      const pfx = state.vinDecoded ? 'VIN read: ' : '';
      if (ok) {
        if (state.trimPendingConfirm) {
          // Trim pre-selected by VIN but not yet confirmed — fitment not loaded yet
          elYmmStatus.textContent = `${pfx}${state.year} · ${state.make} · ${state.model} · ${state.trim} — confirm your trim above to load exact fitment.`;
          elYmmStatus.classList.remove('is-ok');
        } else {
          elYmmStatus.textContent = `${pfx}${state.year} · ${state.make} · ${state.model} · ${state.trim} — fitment loaded.`;
          elYmmStatus.classList.add('is-ok');
        }
      } else if (state.year && state.make && state.model) {
        elYmmStatus.textContent = state.vinDecoded
          ? `VIN read: ${state.year} · ${state.make} · ${state.model} — confirm your trim.`
          : `${state.year} · ${state.make} · ${state.model} — pick a trim to load fitment.`;
        elYmmStatus.classList.remove('is-ok');
      } else {
        elYmmStatus.textContent = 'Pick your year, make, model and trim — we’ll auto-load your exact stock fitment.';
        elYmmStatus.classList.remove('is-ok');
      }
    }

    function resolveStockFit() {
      state.oeTechnical = null; // fallback catalog has no OE technical data
      if (state.trimIdx < 0) { state.stockFit = null; state.brakeNote = ''; return; }
      const trims = lookupTrims(state.model);
      const trim = trims[state.trimIdx];
      state.stockFit = (trim && trim.fit) ? Object.assign({}, trim.fit) : null;
      state.brakeNote = (trim && trim.brake) ? trim.brake : '';
    }

    /* ----- VIN entry mode ----- */
    function switchCarMode(mode) {
      state.carInputMode = mode;
      elCarModeBtns.forEach(btn => {
        const active = btn.getAttribute('data-lt7-cfg-car-mode') === mode;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', String(active));
      });
      elCarPanels.forEach(panel => {
        panel.style.display = panel.getAttribute('data-lt7-cfg-car-panel') === mode ? '' : 'none';
      });
    }

    function showVinStatus(text, type) {
      if (!elVinStatus) return;
      elVinStatus.textContent = text;
      elVinStatus.className = 'lt7-cfg__vin-panel-status lt7-cfg__vin-panel-status--' + type;
      elVinStatus.removeAttribute('hidden');
    }
    function hideVinStatus() {
      if (!elVinStatus) return;
      elVinStatus.setAttribute('hidden', '');
      elVinStatus.textContent = '';
    }

    // R7 — Trim-confirm callout: shown after VIN decode pre-selects a trim.
    // Fitment does not auto-load; user must explicitly confirm before OE data loads.
    function showTrimConfirmCallout() {
      const el = $('[data-lt7-cfg-trim-confirm]');
      if (!el) return;
      el.removeAttribute('hidden');
      // P1-30 FIXPASS: on mobile the mandatory trim-confirm card renders below the
      // configurator's scroll fold (and behind the sticky nav). Defer to next frame so
      // sibling DOM updates in the decode handler settle, then center it inside the
      // actual scroll container (.lt7-cfg__steps) rather than relying on scrollIntoView.
      setTimeout(function () {
        try {
          const steps = root.querySelector('[data-lt7-cfg-steps]');
          if (steps && steps.scrollHeight > steps.clientHeight + 2) {
            const cr = el.getBoundingClientRect();
            const sr = steps.getBoundingClientRect();
            const delta = (cr.top - sr.top) - (steps.clientHeight / 2) + (cr.height / 2);
            steps.scrollBy({ top: delta, behavior: 'smooth' });
          } else {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (_) {
          try { el.scrollIntoView(); } catch (e) {}
        }
      }, 60);
    }
    function hideTrimConfirmCallout() {
      const el = $('[data-lt7-cfg-trim-confirm]');
      if (el) el.setAttribute('hidden', '');
    }

    // Multi-pass NHTSA model name → WS option matcher.
    // Problem: NHTSA returns "CT5" but WS has "CT5-V"; NHTSA returns "Q6" but WS only has "Q6 e-tron".
    // Pass 1 (exact): stripped slug/text equals stripped NHTSA model.
    // Pass 2 (prefix): WS slug starts with stripped NHTSA model.
    // When multiple candidates found, score by confirmed vs unconfirmed extra words against
    // NHTSA Trim+Series — the candidate with the best score wins.
    //   CT5 + Trim "V-Blackwing" → CT5-V wins (extra word "v" confirmed from trim).
    //   Q6  + Trim "e-tron …"    → Q6 e-tron wins over Q6 Sportback e-tron ("sportback" unconfirmed).
    function matchWsModel(nhtsaModel, nhtsaTrim, nhtsaSeries, modelOptions) {
      function strip(s) { return (s || '').toLowerCase().replace(/[\s\-\.]/g, ''); }
      function splitWords(s) { return (s || '').toLowerCase().split(/[\s\-\.\/]+/).filter(function(w) { return w.length >= 1; }); }
      var modelNorm = strip(nhtsaModel);
      var modelWords = splitWords(nhtsaModel);
      var trimWords = splitWords((nhtsaTrim || '') + ' ' + (nhtsaSeries || ''));
      var allNhtsaWords = {};
      modelWords.concat(trimWords).forEach(function(w) { allNhtsaWords[w] = true; });

      var candidates = [];
      for (var i = 0; i < modelOptions.length; i++) {
        var o = modelOptions[i];
        if (!o.value || o.value === '__request__') continue;
        var vStrip = strip(o.value);
        var tStrip = strip(o.text);
        if (vStrip === modelNorm || tStrip === modelNorm ||
            vStrip.startsWith(modelNorm) || tStrip.startsWith(modelNorm)) {
          candidates.push(o);
        }
      }
      if (candidates.length === 0) return null;
      if (candidates.length === 1) return candidates[0];

      var best = candidates[0], bestScore = -Infinity;
      for (var j = 0; j < candidates.length; j++) {
        var opt = candidates[j];
        var optWords = splitWords(opt.text);
        var extra = optWords.filter(function(w) { return modelWords.indexOf(w) === -1; });
        var confirmed = extra.filter(function(w) { return allNhtsaWords[w]; }).length;
        var unconfirmed = extra.length - confirmed;
        var score = confirmed - unconfirmed * 0.5;
        if (score > bestScore) { bestScore = score; best = opt; }
      }
      return best;
    }

    // Decode a VIN via NHTSA vPIC API — free, no key, CORS-enabled public API.
    async function decodeNhtsaVin(vin) {
      try {
        const r = await fetch(
          'https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues/' + encodeURIComponent(vin) + '?format=json',
          { headers: { Accept: 'application/json' } }
        );
        if (!r.ok) return null;
        const j = await r.json();
        const res = j && j.Results && j.Results[0];
        if (!res) return null;
        const year  = (res.ModelYear || '').trim();
        const make  = (res.Make  || '').trim();
        const model = (res.Model || '').trim();
        if (!year || !make || !model || parseInt(year, 10) < 2005) return null;
        return {
          year,
          make,
          model,
          series: (res.Series || '').trim(),
          trim:   (res.Trim   || '').trim(),
          disp:   (res.DisplacementL || '').trim(),
        };
      } catch (_) { return null; }
    }

    function toTitleCase(s) {
      return (s || '').toLowerCase().replace(/(?:^|\s)\S/g, c => c.toUpperCase());
    }

    async function handleVinDecode() {
      if (!elVinInput) return;
      const raw = elVinInput.value.trim().toUpperCase();

      // 17 chars, alphanumeric, no I/O/Q (VIN spec excludes those)
      if (!/^[A-HJ-NPR-Z0-9]{17}$/.test(raw)) {
        showVinStatus(
          'Please enter a valid 17-character VIN — letters and numbers only (I, O, and Q are not used in VINs).',
          'error'
        );
        return;
      }

      showVinStatus('Decoding VIN…', 'loading');
      if (elVinDecode) elVinDecode.disabled = true;

      const decoded = await decodeNhtsaVin(raw);

      if (!decoded) {
        showVinStatus(
          'Couldn’t read that VIN automatically — email it to info@ltforgedwheels.com and we’ll spec it one-on-one.',
          'concierge'
        );
        if (elVinDecode) elVinDecode.disabled = false;
        return;
      }

      // Set year + repopulate makes
      state.year = decoded.year;
      if (elYear) elYear.value = decoded.year;
      state.make = ''; state.makeSlug = '';
      state.model = ''; state.modelSlug = '';
      state.trim = ''; state.trimIdx = -1;
      state.liveModels = null; state.liveMods = null; state.stockFit = null;
      await populateMakes();

      // Match make (NHTSA returns ALL CAPS)
      const makeLower = decoded.make.toLowerCase();
      const makeOpt = Array.from(elMake.options).find(o =>
        o.value && o.value !== '__request__' && o.text.toLowerCase() === makeLower
      );
      if (!makeOpt) {
        showVinStatus(
          toTitleCase(decoded.make) + ' isn’t in our fitment database yet — email your VIN to info@ltforgedwheels.com and we’ll spec it one-on-one.',
          'concierge'
        );
        if (elVinDecode) elVinDecode.disabled = false;
        return;
      }

      elMake.value = makeOpt.value;
      state.makeSlug = makeOpt.value;
      state.make = makeOpt.text;
      await populateModels();

      // Match model using multi-pass matcher (handles CT5→CT5-V, Q6→Q6 e-tron naming gaps)
      const modelOpt = matchWsModel(decoded.model, decoded.trim, decoded.series, Array.from(elModel.options));

      if (!modelOpt) {
        // Make found but model not in WS — pre-fill year/make, let user pick model
        state.vinDecoded = true;
        hideVinStatus();
        switchCarMode('picker');
        if (elModelReach) elModelReach.removeAttribute('hidden');
        if (elYmmStatus) {
          elYmmStatus.textContent = 'VIN read: ' + decoded.year + ' · ' + state.make + ' — select your model to continue.';
          elYmmStatus.classList.remove('is-ok');
        }
        elModel.innerHTML = '<option value="">Model</option>';
        elModel.disabled = false;
        elTrim.innerHTML = '<option value="">Trim</option>';
        elTrim.disabled = true;
        renderSizingStock();
        updateNav();
        if (elVinDecode) elVinDecode.disabled = false;
        return;
      }

      elModel.value = modelOpt.value;
      state.modelSlug = modelOpt.value;
      state.model = modelOpt.text;
      await populateTrims();

      // Best-effort trim match from NHTSA Series + Trim fields.
      // R7: pre-selects the trim dropdown but does NOT auto-load fitment.
      // User must explicitly confirm via the trim-confirm callout before OE data loads.
      const vinTrimHint = [decoded.series, decoded.trim].filter(Boolean).join(' ').toLowerCase().trim();
      if (vinTrimHint && state.liveMods && state.liveMods.length) {
        let bestIdx = -1, bestScore = 0;
        state.liveMods.forEach(function(t, i) {
          const lbl = t.label.toLowerCase();
          const words = vinTrimHint.split(/\s+/).filter(function(w) { return w.length > 2; });
          const score = words.filter(function(w) { return lbl.includes(w); }).length;
          if (score > bestScore) { bestScore = score; bestIdx = i; }
        });
        if (bestIdx >= 0 && bestScore > 0) {
          elTrim.value = String(bestIdx);
          state.trimIdx = bestIdx;
          state.trim = state.liveMods[bestIdx].label;
          state.trimPendingConfirm = true; // require explicit confirm — fitment loads on button click or trim change
          showTrimConfirmCallout();
        }
      }

      renderSizingStock();
      showBrakeNote(state.brakeNote);
      if (elTrimReach) elTrimReach.removeAttribute('hidden');

      // Success: switch to picker so user can see + confirm pre-filled result
      state.vinDecoded = true;
      hideVinStatus();
      switchCarMode('picker');
      updateYmmStatus();
      updateNav();
      if (elVinDecode) elVinDecode.disabled = false;
    }

    /* ----- Sizing (FITMERGE: the merged fitment tool is the single source of truth) ----- */
    // Parse a tyre string "275/35R19" → {tyreW:275, aspect:35}; tolerant of spaces / P / ZR.
    function parseTire(t) {
      const m = String(t == null ? '' : t).match(/(\d{3})\s*\/\s*(\d{2})/);
      return m ? { tyreW: parseInt(m[1], 10), aspect: parseInt(m[2], 10) } : null;
    }
    // Reconstruct a tyre string from the tool's structured build spec.
    function tireStr(w, a, d) { return (w && a && d) ? (w + '/' + a + 'R' + d) : ''; }
    function numOr(v, fb) { const n = parseFloat(v); return isNaN(n) ? (fb === undefined ? '' : fb) : n; }

    // Push the DB stock fitment into the tool (fills read-only chips + the stock-match build default).
    function pushStockToTool() {
      if (!window.LTF || !window.LTF.setStock || !state.stockFit) return;
      const s = state.stockFit;
      const pf = parseTire(s.ft) || {}, pr = parseTire(s.rt) || {};
      window.LTF.setStock('front', { dia: numOr(s.fd), width: numOr(s.fw), offset: numOr(s.fo), tyreW: pf.tyreW, aspect: pf.aspect });
      window.LTF.setStock('rear',  { dia: numOr(s.rd), width: numOr(s.rw), offset: numOr(s.ro), tyreW: pr.tyreW, aspect: pr.aspect });
    }

    // Retained chokepoint — every stock/trim change already calls renderSizingStock(). It now
    // syncs the tool (stock-in) and re-reads the build (out) into state.
    function renderSizingStock() { pushStockToTool(); readCustomFit(); }

    // Vestigial — the old stock/custom toggle is gone; mode is DERIVED in readCustomFit().
    function setSizingMode(mode) { state.sizingMode = mode; }

    // Does the tool's BUILD equal the DB stock fitment? (numeric compare; tyre by width/aspect)
    function buildEqualsStock() {
      if (!state.stockFit || !window.LTF || !window.LTF.getBuild) return false;
      const s = state.stockFit;
      const bf = window.LTF.getBuild('front'), br = window.LTF.getBuild('rear');
      if (!bf || !br) return false;
      const eqRim = (b, d, w, o) => Number(b.dia) === numOr(d) && Number(b.width) === numOr(w) && Number(b.offset) === numOr(o);
      const eqTyre = (b, t) => { const p = parseTire(t); if (!p) return true; return Number(b.tyreW) === p.tyreW && Number(b.aspect) === p.aspect; };
      return eqRim(bf, s.fd, s.fw, s.fo) && eqTyre(bf, s.ft) && eqRim(br, s.rd, s.rw, s.ro) && eqTyre(br, s.rt);
    }

    // readCustomFit pulls the editable BUILD spec from the merged tool (the order's fitment),
    // reconstructs tyre strings, and derives sizingMode (stock if build==stock, else custom).
    function readCustomFit() {
      if (window.LTF && window.LTF.getBuild) {
        const bf = window.LTF.getBuild('front'), br = window.LTF.getBuild('rear');
        if (bf) { state.customFit.fd = String(bf.dia); state.customFit.fw = String(bf.width); state.customFit.fo = String(bf.offset); state.customFit.ft = tireStr(bf.tyreW, bf.aspect, bf.dia); }
        if (br) { state.customFit.rd = String(br.dia); state.customFit.rw = String(br.width); state.customFit.ro = String(br.offset); state.customFit.rt = tireStr(br.tyreW, br.aspect, br.dia); }
      }
      state.sizingMode = buildEqualsStock() ? 'stock' : 'custom';
    }

    // Advisory fit-check line for the step-5 Review (additive row).
    function fitCheckLine() {
      if (!window.LTF || !window.LTF.getVerdict) return '—';
      const W = { fits: 'FITS', rub: 'MIGHT RUB', wont: "WON'T CLEAR" };
      const fmt = (v) => v ? (W[v.state] + ' (' + (v.clearance >= 0 ? '+' : '−') + Math.abs(v.clearance) + ' mm)') : '—';
      return 'Front: ' + fmt(window.LTF.getVerdict('front')) + ' · Rear: ' + fmt(window.LTF.getVerdict('rear'));
    }

    // P1-8 FIXPASS: format an offset as "(+28)" / "(-12)" regardless of how the
    // user typed it ("+28", "28", "(+28)") — fixes the "(++28)" double-sign bug.
    function offTag(v) {
      if (v === undefined || v === null) return '';
      let s = String(v).replace(/[()\s]/g, '');
      if (s === '') return '';
      s = s.replace(/^\+/, '');
      if (s === '' || s === '-') return '';
      const sign = s.charAt(0) === '-' ? '' : '+';
      return '(' + sign + s + ')';
    }

    function sizingSummary() {
      if (state.sizingMode === 'stock' && state.stockFit) {
        const f = state.stockFit;
        return `${f.fd}×${f.fw} ${offTag(f.fo)} ${f.ft} F / ${f.rd}×${f.rw} ${offTag(f.ro)} ${f.rt} R · stock fitment`;
      }
      if (state.sizingMode === 'custom') {
        readCustomFit();
        const c = state.customFit;
        const front = (c.fd && c.fw) ? `${c.fd}×${c.fw}${c.fo?offTag(c.fo):''}${c.ft?' '+c.ft:''}` : '—';
        const rear  = (c.rd && c.rw) ? `${c.rd}×${c.rw}${c.ro?offTag(c.ro):''}${c.rt?' '+c.rt:''}` : '—';
        return `${front} F / ${rear} R · custom`;
      }
      // P1-28 FIXPASS: removed "after deposit" — locked flow is charge-in-full at checkout.
      return 'Concierge will confirm fitment after your order.';
    }

    function sizingValid() {
      // Verdict is advisory and NEVER gates — we only require a vehicle + build numbers present.
      const hasVehicle = !!(state.year && state.make && state.model);
      if (!hasVehicle) return false;
      if (!window.LTF || !window.LTF.getBuild) return true;
      const bf = window.LTF.getBuild('front'), br = window.LTF.getBuild('rear');
      return !!(bf && br && bf.dia && bf.width && br.dia && br.width);
    }

    /* ----- Canvas ----- */
    function updateCanvas() {
      if (elCanvasImg) {
        elCanvasImg.classList.add('is-changing');
        setTimeout(() => elCanvasImg.classList.remove('is-changing'), 280);
        if (state.finish && state.finish.colorHex) {
          const h = hashHue(state.finish.colorHex || state.finish.color || state.finish.name);
          elCanvasImg.style.filter =
            `drop-shadow(0 24px 48px rgba(0,0,0,0.55)) hue-rotate(${h}deg) saturate(1.05)`;
        }
      }
      if (elCanvasZone) {
        const zones = ['CAR', 'FINISH', 'CAP', 'FITMENT', 'REVIEW'];
        elCanvasZone.textContent = zones[state.step] || '';
      }
    }
    function hashHue(s) {
      if (!s) return 0;
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
      return Math.round(h / 6) - 30;
    }

    /* ----- Steps ----- */
    function setStep(idx) {
      idx = Math.max(0, Math.min(STEPS.length - 1, idx));
      state.step = idx;
      hideNavHint();

      // FITSTEP 2026-06-10: Step 4 (index 3 = Fitment) takes over the full stage — hide the
      // wheel-preview canvas and let the fitment tool use the whole content area. Canvas is
      // only CSS-hidden (state preserved); it returns intact on Step 3 / Step 5.
      if (elLayout) elLayout.classList.toggle('lt7--fit-fullstage', idx === 3);

      // 2E: step transition — brief opacity fade before swap
      elSteps.forEach((el) => { el.style.opacity = '0'; el.style.transition = 'opacity 140ms ease'; });
      requestAnimationFrame(() => {
        elSteps.forEach((el, i) => {
          el.classList.toggle('is-active', i === idx);
          el.style.opacity = i === idx ? '1' : '';
          el.style.transition = i === idx ? 'opacity 220ms ease' : '';
        });
      });

      elProgress.forEach((el, i) => {
        el.classList.remove('is-active', 'is-done');
        if (i === idx) {
          el.classList.add('is-active');
          el.setAttribute('aria-current', 'step');
        } else {
          el.removeAttribute('aria-current');
          if (i < idx) el.classList.add('is-done');
        }
      });
      if (elStepNum)  elStepNum.textContent = `Step ${idx + 1} / ${STEPS.length}`;
      if (elStepName) elStepName.textContent = STEPS[idx].name;

      const stepsHost = root.querySelector('[data-lt7-cfg-steps]');
      if (stepsHost) {
        try { stepsHost.scrollTo({ top: 0, behavior: 'auto' }); }
        catch (_) { stepsHost.scrollTop = 0; }
      }

      if (idx === 4) renderReview();

      updateCanvas();
      updateNav();
      applyLayoutMetrics(root);
    }

    function stepSatisfied(idx) {
      switch (idx) {
        case 0: return !!(state.year && state.make && state.model && state.trim) && !state.trimPendingConfirm; // P1-30 FIXPASS: don't let a VIN-decoded build advance until the trim is explicitly confirmed
        case 1: return !!state.finish;
        case 2: return !!state.cap;
        case 3: return sizingValid();
        case 4: return allGatesTicked() && stepSatisfied(0) && !!state.finish && sizingValid();
        default: return false;
      }
    }

    function allGatesTicked() {
      return GATE_KEYS.every(k => state.gates[k] === true);
    }

    function updateNav() {
      const idx = state.step;
      const isLast = idx === STEPS.length - 1;
      const satisfied = stepSatisfied(idx);

      if (elNavBack) {
        elNavBack.disabled = idx === 0;
        elNavBack.setAttribute('aria-disabled', String(idx === 0));
      }
      if (elNavNext) {
        const can = satisfied;
        elNavNext.disabled = !can || isLast;
        elNavNext.style.display = isLast ? 'none' : '';
        elNavNext.setAttribute('aria-disabled', String(!can || isLast));
        const label = STEPS[idx].ctaNext || 'Continue';
        rewriteBtnText(elNavNext, label);
      }
      if (elNavLock) {
        elNavLock.style.display = isLast ? '' : 'none';
        const can = stepSatisfied(4);
        elNavLock.disabled = !can;
        elNavLock.setAttribute('aria-disabled', String(!can));
        rewriteBtnText(elNavLock, can ? 'Lock my build' : 'Tick all gates to lock');
      }
      if (elNavMeta) {
        const stepName = STEPS[idx].name;
        elNavMeta.innerHTML = `Step <strong>${idx + 1}</strong> of <strong>${STEPS.length}</strong> · ${stepName} · <span class="lt7-cfg__navbar-meta-price">${perSetMoney}</span>`;
      }

      // Show/hide inline hint under navbar
      if (!satisfied) {
        showNavHint(STEP_HINT[idx] || '');
      } else {
        hideNavHint();
      }
    }

    function rewriteBtnText(btn, text) {
      let textNode = null;
      for (let i = 0; i < btn.childNodes.length; i++) {
        if (btn.childNodes[i].nodeType === 3) { textNode = btn.childNodes[i]; break; }
      }
      if (textNode) textNode.nodeValue = text;
      else btn.insertBefore(document.createTextNode(text), btn.firstChild);
    }

    /* ----- Review ----- */
    function renderReview() {
      readCustomFit(); // FITMERGE: refresh order fitment + derived mode from the tool before summarising
      if (elReviewVehicle) {
        if (state.year && state.make && state.model) {
          elReviewVehicle.textContent = `${state.year} · ${state.make} · ${state.model}`;
          elReviewVehicle.classList.remove('lt7-cfg__review-row-v--empty');
        } else {
          elReviewVehicle.textContent = 'Not yet set';
          elReviewVehicle.classList.add('lt7-cfg__review-row-v--empty');
        }
      }
      if (elReviewTrim) {
        if (state.trim) {
          elReviewTrim.textContent = state.trim;
          elReviewTrim.classList.remove('lt7-cfg__review-row-v--empty');
        } else {
          elReviewTrim.textContent = 'Pick a trim on Step 01';
          elReviewTrim.classList.add('lt7-cfg__review-row-v--empty');
        }
      }
      if (elReviewFace) elReviewFace.textContent = state.face;
      if (elReviewFinish) {
        if (state.finish && state.finish.finish) {
          elReviewFinish.textContent = state.finish.finish;
          elReviewFinish.classList.remove('lt7-cfg__review-row-v--empty');
        } else {
          elReviewFinish.textContent = 'Pick a finish on Step 02';
          elReviewFinish.classList.add('lt7-cfg__review-row-v--empty');
        }
      }
      if (elReviewColor) {
        if (state.finish && state.finish.color) {
          elReviewColor.textContent = state.finish.color;
          elReviewColor.classList.remove('lt7-cfg__review-row-v--empty');
        } else {
          elReviewColor.textContent = 'Pick a color on Step 02';
          elReviewColor.classList.add('lt7-cfg__review-row-v--empty');
        }
      }
      // P1-6 FIXPASS: Cap review row now reflects the chosen cap colour, not just "Stock".
      if (elReviewCap) {
        elReviewCap.textContent = (state.cap === 'Color-matched body')
          ? state.cap
          : (state.capColor ? `${state.cap} · ${state.capColor}` : state.cap);
      }
      if (elReviewSizing) elReviewSizing.textContent = sizingSummary();
      if (elReviewFitcheck) elReviewFitcheck.textContent = fitCheckLine();
    }

    /* ----- Wiring ----- */
    function wire() {
      populateYears();
      populateMakes();
      populateModels();
      elTrim.disabled = true;
      updateYmmStatus();
      renderSizingStock();
      setSizingMode('stock');

      elYear  && elYear.addEventListener('change',  async () => {
        state.year = elYear.value;
        state.vinDecoded = false;
        state.trimPendingConfirm = false;
        hideTrimConfirmCallout();
        if (WS_ENABLED) {
          state.make = ''; state.makeSlug = '';
          state.model = ''; state.modelSlug = '';
          state.trim = ''; state.trimIdx = -1; state.brakeNote = '';
          state.liveModels = null; state.liveMods = null; state.stockFit = null; state.oeTechnical = null;
          await populateMakes();
          elModel.innerHTML = '<option value="">Model</option>'; elModel.disabled = true;
          elTrim.innerHTML = '<option value="">Trim</option>';  elTrim.disabled = true;
          hideBrakeNote();
          renderSizingStock();
        }
        if (elModelReach) elModelReach.setAttribute('hidden', '');
        if (elTrimReach)  elTrimReach.setAttribute('hidden', '');
        updateYmmStatus();
        updateNav();
      });
      elMake  && elMake.addEventListener('change',  async () => {
        const v = elMake.value;
        // "Request it" option: open mailto and reset selector
        if (v === '__request__') {
          window.open('mailto:info@ltforgedwheels.com?subject=Fitment%20request%20%E2%80%94%20JDM%20%2F%20modified%20chassis', '_blank');
          elMake.value = '';
          return;
        }
        state.vinDecoded = false;
        state.trimPendingConfirm = false;
        hideTrimConfirmCallout();
        state.makeSlug = (WS_ENABLED && state.liveMakes) ? v : '';
        state.make = v ? (((elMake.options[elMake.selectedIndex] || {}).text) || '') : '';
        state.model = ''; state.modelSlug = '';
        state.trim = ''; state.trimIdx = -1; state.brakeNote = '';
        state.liveMods = null; state.stockFit = null;
        await populateModels();
        elTrim.innerHTML = '<option value="">Trim</option>';
        elTrim.disabled = true;
        hideBrakeNote();
        if (!(WS_ENABLED && state.liveModels)) resolveStockFit();
        renderSizingStock();
        // Show model reach-out link once models are loaded (helps JDM users find the email path)
        if (elModelReach) { if (state.make) elModelReach.removeAttribute('hidden'); else elModelReach.setAttribute('hidden', ''); }
        if (elTrimReach) elTrimReach.setAttribute('hidden', '');
        updateYmmStatus();
        updateNav();
      });
      elModel && elModel.addEventListener('change', async () => {
        const v = elModel.value;
        state.trimPendingConfirm = false;
        hideTrimConfirmCallout();
        state.modelSlug = (WS_ENABLED && state.liveModels) ? v : '';
        state.model = v ? (((elModel.options[elModel.selectedIndex] || {}).text) || '') : '';
        await populateTrims();
        if (!(WS_ENABLED && state.liveMods)) resolveStockFit();
        else state.stockFit = null;
        renderSizingStock();
        // Show trim reach-out link once trims are loaded (helps JDM/usdm-filtered users)
        if (elTrimReach) { if (state.model) elTrimReach.removeAttribute('hidden'); else elTrimReach.setAttribute('hidden', ''); }
        updateYmmStatus();
        updateNav();
      });
      elTrim && elTrim.addEventListener('change', async () => {
        // Manual trim change = explicit confirm — clear pending-confirm state and hide callout
        state.trimPendingConfirm = false;
        hideTrimConfirmCallout();
        const v = elTrim.value;
        if (v === '') {
          state.trim = '';
          state.trimIdx = -1;
          state.stockFit = null;
          state.oeTechnical = null;
          state.brakeNote = '';
        } else {
          const idx = parseInt(v, 10);
          state.trimIdx = idx;
          if (WS_ENABLED && state.liveMods) {
            const t = state.liveMods[idx];
            state.trim = t ? t.label : '';
            await loadLiveFitment();
          } else {
            const trims = lookupTrims(state.model);
            const t = trims[idx];
            state.trim = t ? t.label : '';
            resolveStockFit();
          }
        }
        renderSizingStock();
        showBrakeNote(state.brakeNote);
        updateYmmStatus();
        updateNav();
      });

      // ── FINISH × COLOUR picker — rendered from LTF_FINISHES / LTF_COLORS config ──
      // Finish cards (8) then colour chips (49). One finish + one colour at a time.
      // state.finish = { finishId, finish, color, colorHex, name }.
      renderFinishColor();
      function renderFinishColor() {
        const finishHost = root.querySelector('[data-lt7-cfg-finish-cards]');
        const colorHost  = root.querySelector('[data-lt7-cfg-color-grid]');
        if (!finishHost || !colorHost) return; // markup absent — nothing to wire

        function syncName() {
          state.finish.name = state.finish.finish + ' · ' + state.finish.color;
        }

        // Build finish cards
        finishHost.innerHTML = '';
        LTF_FINISHES.forEach(f => {
          const sel = state.finish.finishId === f.id;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'lt7-fc-card' + (sel ? ' is-selected' : '');
          btn.setAttribute('data-lt7-cfg-finish', '');
          btn.setAttribute('data-finish-id', f.id);
          btn.setAttribute('aria-pressed', sel ? 'true' : 'false');
          btn.setAttribute('aria-label', f.name + ' finish');
          btn.innerHTML =
            '<span class="lt7-fc-card__flag" aria-hidden="true">' + f.flag + '</span>' +
            '<span class="lt7-fc-card__name">' + f.name + '</span>' +
            '<span class="lt7-fc-card__desc">' + f.desc + '</span>';
          btn.addEventListener('click', () => {
            finishHost.querySelectorAll('.lt7-fc-card').forEach(c => {
              c.classList.remove('is-selected'); c.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add('is-selected'); btn.setAttribute('aria-pressed', 'true');
            state.finish.finishId = f.id;
            state.finish.finish   = f.name;
            syncName();
            updateCanvas();
            updateNav();
          });
          finishHost.appendChild(btn);
        });

        // Build colour chips
        colorHost.innerHTML = '';
        LTF_COLORS.forEach(c => {
          const sel = state.finish.color === c.name;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'lt7-fc-chip' + (sel ? ' is-selected' : '');
          btn.setAttribute('data-lt7-cfg-color', '');
          btn.setAttribute('data-color-name', c.name);
          btn.setAttribute('data-color-hex', c.hex);
          btn.setAttribute('aria-pressed', sel ? 'true' : 'false');
          btn.setAttribute('aria-label', c.name);
          btn.setAttribute('title', c.name);
          btn.innerHTML =
            '<span class="lt7-fc-chip__sw" style="--fc-chip:' + c.hex + ';" aria-hidden="true"></span>' +
            '<span class="lt7-fc-chip__label">' + c.name + '</span>';
          btn.addEventListener('click', () => {
            colorHost.querySelectorAll('.lt7-fc-chip').forEach(s => {
              s.classList.remove('is-selected'); s.setAttribute('aria-pressed', 'false');
            });
            btn.classList.add('is-selected'); btn.setAttribute('aria-pressed', 'true');
            state.finish.color    = c.name;
            state.finish.colorHex = c.hex;
            syncName();
            updateCanvas();
            updateNav();
          });
          colorHost.appendChild(btn);
        });
      }

      // Pass 3: cap tiles get aria-pressed
      root.querySelectorAll('[data-lt7-cfg-cap]').forEach(t => {
        if (!t.getAttribute('aria-pressed')) t.setAttribute('aria-pressed', t.classList.contains('is-selected') ? 'true' : 'false');
        t.addEventListener('click', () => {
          root.querySelectorAll('[data-lt7-cfg-cap]').forEach(s => {
            s.classList.remove('is-selected');
            s.setAttribute('aria-pressed', 'false');
          });
          t.classList.add('is-selected');
          t.setAttribute('aria-pressed', 'true');
          state.cap = t.getAttribute('data-name');
          const colorSection = root.querySelector('[data-lt7-cfg-cap-color-section]');
          if (colorSection) {
            const isColorMatch = state.cap === 'Color-matched body';
            colorSection.style.opacity = isColorMatch ? '0.35' : '1';
            colorSection.style.pointerEvents = isColorMatch ? 'none' : '';
          }
          updateCapSwatchPreview();
          updateNav();
        });
      });

      root.querySelectorAll('[data-lt7-cfg-cap-color]').forEach(sw => {
        sw.addEventListener('click', () => {
          root.querySelectorAll('[data-lt7-cfg-cap-color]').forEach(s => s.classList.remove('is-selected'));
          sw.classList.add('is-selected');
          state.capColor = sw.getAttribute('data-cap-color-name');
          state.capColorHex = sw.getAttribute('data-cap-color');
          updateCapSwatchPreview();
          updateNav();
        });
      });

      function updateCapSwatchPreview() {
        const stockPreview = root.querySelector('[data-lt7-cfg-cap][data-name="Stock · cyan + white"] .lt7-cfg__tile-cap-swatch:not(.lt7-cfg__tile-cap-swatch--match):not(.lt7-cfg__tile-cap-swatch--custom)');
        if (stockPreview && state.cap === 'Stock · cyan + white') {
          stockPreview.style.background = state.capColorHex;
        }
      }

      // FITMERGE: the merged tool is the single source. Subscribe to its build/stock/gap changes
      // → re-read the order fitment into state, refresh nav gating + the Review fit-check line.
      if (window.LTF && window.LTF.onChange) {
        window.LTF.onChange(function () {
          readCustomFit();
          updateNav();
          if (state.step === 4 && elReviewFitcheck) elReviewFitcheck.textContent = fitCheckLine();
        });
      }

      elNavBack && elNavBack.addEventListener('click', () => setStep(state.step - 1));
      elNavNext && elNavNext.addEventListener('click', () => {
        if (stepSatisfied(state.step)) {
          setStep(state.step + 1);
        } else {
          // Make hint visible and pulse it for attention
          showNavHint(STEP_HINT[state.step] || '');
          if (elNavHint) {
            elNavHint.classList.remove('lt7-cfg__nav-hint--pulse');
            void elNavHint.offsetWidth; // reflow to restart animation
            elNavHint.classList.add('lt7-cfg__nav-hint--pulse');
          }
        }
      });

      root.querySelectorAll('[data-lt7-cfg-gate]').forEach(cb => {
        cb.addEventListener('change', () => {
          const key = cb.getAttribute('data-lt7-cfg-gate');
          if (GATE_KEYS.indexOf(key) === -1) return;
          state.gates[key] = !!cb.checked;
          updateNav();
        });
      });

      elNavLock && elNavLock.addEventListener('click', async () => {
        if (!stepSatisfied(4)) return;
        await submitToCart();
      });

      // Car mode toggle (Picker ↔ VIN)
      elCarModeBtns.forEach(function(btn) {
        btn.addEventListener('click', function() {
          switchCarMode(btn.getAttribute('data-lt7-cfg-car-mode') || 'picker');
        });
      });

      // VIN decode — button click + Enter key
      if (elVinDecode) elVinDecode.addEventListener('click', handleVinDecode);
      if (elVinInput) {
        elVinInput.addEventListener('keydown', function(e) {
          if (e.key === 'Enter') { e.preventDefault(); handleVinDecode(); }
        });
        elVinInput.addEventListener('input', function() {
          hideVinStatus();
        });
      }

      // R7 — Trim-confirm button: user clicks to explicitly confirm VIN-pre-selected trim
      const elTrimConfirmBtn = $('[data-lt7-cfg-trim-confirm-btn]');
      if (elTrimConfirmBtn) {
        elTrimConfirmBtn.addEventListener('click', async function() {
          if (state.trimIdx < 0) return;
          state.trimPendingConfirm = false;
          hideTrimConfirmCallout();
          await loadLiveFitment();
          renderSizingStock();
          showBrakeNote(state.brakeNote);
          updateYmmStatus();
          updateNav();
        });
      }

      setStep(0);

      if (!hasOwnRender && elCanvasNote) {
        elCanvasNote.style.display = '';
      }
    }

    // P1-27 FIXPASS: surface a retryable error if /cart/add.js fails (was a silent dead-end).
    function showCartError(msg) {
      let el = root.querySelector('[data-lt7-cfg-cart-error]');
      if (!el) {
        el = document.createElement('p');
        el.setAttribute('data-lt7-cfg-cart-error', '');
        el.setAttribute('role', 'alert');
        el.className = 'lt7-cfg__cart-error';
        const nav = root.querySelector('[data-lt7-cfg-navbar]');
        if (nav && nav.parentNode) nav.parentNode.insertBefore(el, nav);
        else root.appendChild(el);
      }
      el.textContent = msg;
      el.style.display = '';
    }
    function clearCartError() {
      const el = root.querySelector('[data-lt7-cfg-cart-error]');
      if (el) { el.style.display = 'none'; el.textContent = ''; }
    }

    let _cartBusy = false;
    async function submitToCart() {
      if (!variantId) return;
      if (_cartBusy) return; // P1-26 FIXPASS: double-submit guard
      _cartBusy = true;
      const lockBtn = elNavLock;
      const lockLabel = lockBtn ? lockBtn.textContent : '';
      if (lockBtn) {
        lockBtn.disabled = true;
        lockBtn.setAttribute('aria-disabled', 'true');
        lockBtn.textContent = 'Locking…';
      }
      clearCartError();
      const fd = new FormData();
      fd.append('id', variantId);
      fd.append('quantity', '4');

      // Helper: format a wheel spec as "Diam×Width  ET+Offset  Tire"
      const specFmt = function(d, w, o, t) {
        const dw = (d && w) ? d + '×' + w : (d || w || '');
        const n = parseFloat((String(o || '')).replace(/^\+/, ''));
        const et = (o !== '' && o != null && !isNaN(n)) ? 'ET' + (n >= 0 ? '+' : '') + Math.round(n) : '';
        return [dw, et, t].filter(Boolean).join('  ').trim();
      };

      readCustomFit(); // FITMERGE: pull the latest BUILD spec + derived mode from the tool before props

      const props = {
        // — Vehicle identification —
        'Vehicle':          `${state.year} · ${state.make} · ${state.model}`,
        'Trim':             state.trim,
        // — Design / finish — (FINISH × COLOUR, captured as two explicit axes)
        'Face':              state.face,
        'Finish':            state.finish ? state.finish.finish : '',
        'Color':             state.finish ? state.finish.color : '',
        '_Color hex':        state.finish ? state.finish.colorHex : '',
        'Cap':               state.cap,
        'Cap color':         state.cap === 'Color-matched body' ? 'matched' : state.capColor,
        // — Fitment summary (human readable) —
        'Fitment':          sizingSummary(),
        'Fitment mode':     state.sizingMode,
        // — Build metadata —
        'Product handle':   productHandle,
        'Selected at':      new Date().toISOString(),
        '_Terms':           'accepted',
        '_Lead_time':       '8–12 weeks',
        '_Spec_confirm':    '48 hours',
      };

      // — OE reference data from Wheel-Size API (R7: confirmed present in technical block) —
      if (state.oeTechnical) {
        const ot = state.oeTechnical;
        if (ot.boltPattern) props['_OE bolt pattern'] = ot.boltPattern;
        if (ot.centreBore)  props['_OE center bore']  = ot.centreBore + ' mm';
        if (ot.lugThread || ot.lugType) {
          props['_OE lug spec'] = [ot.lugType, ot.lugThread, ot.torque].filter(Boolean).join(' / ');
        }
      }

      // — Customer's chosen wheel spec (factory-ready, per-axle) —
      let _fd = '', _fw = '', _fo = '', _ft = '', _rd = '', _rw = '', _ro = '', _rt = '', _stag = 'No';
      if (state.sizingMode === 'stock' && state.stockFit) {
        const f = state.stockFit;
        _fd = f.fd; _fw = f.fw; _fo = f.fo; _ft = f.ft;
        _rd = f.rd; _rw = f.rw; _ro = f.ro; _rt = f.rt;
        _stag = f.isStaggered ? 'Yes' : 'No';
      } else if (state.sizingMode === 'custom') {
        readCustomFit();
        const c = state.customFit;
        _fd = c.fd; _fw = c.fw; _fo = c.fo; _ft = c.ft;
        _rd = c.rd; _rw = c.rw; _ro = c.ro; _rt = c.rt;
        _stag = (_fd && _rd && _fw && _rw && (_fd !== _rd || _fw !== _rw)) ? 'Yes' : 'No';
      }
      const _wf = specFmt(_fd, _fw, _fo, _ft);
      const _wr = specFmt(_rd, _rw, _ro, _rt);
      if (_wf) props['Wheel front'] = _wf;
      if (_wr) props['Wheel rear']  = _wr;
      props['Staggered']    = _stag;
      // P1-9 FIXPASS: derive construction from the product family instead of hardcoding "monoblock".
      (function () {
        const code = String(state.face || productFace || productHandle || '').toUpperCase();
        let c = 'Forged 6061-T6';
        if (code.indexOf('MAG') !== -1)      c = 'Forged magnesium';
        else if (code.indexOf('LTB') === 0)  c = 'Forged 6061-T6 beadlock';
        else if (code.indexOf('LTT') === 0)  c = 'Forged 6061-T6 turbo';
        else if (code.indexOf('LT2') === 0)  c = 'Forged 6061-T6 two-piece';
        else if (code.indexOf('LTM') === 0)  c = 'Forged 6061-T6 monoblock';
        props['Construction'] = c;
      })();
      props['Set qty']      = '4 wheels';
      Object.entries(props).forEach(([k, v]) => fd.append('properties[' + k + ']', v));

      try {
        const payload = { id: variantId, quantity: '4', properties: props };
        window.__LT7_LAST_CART_ADD = payload;
        window.dispatchEvent(new CustomEvent('lt7:cart-add-prepared', { detail: payload }));
      } catch (_) {}

      try {
        const r = await fetch('/cart/add.js', {
          method: 'POST', body: fd, credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });
        if (r.ok) { window.location.href = '/cart'; return; } // success: keep button disabled through navigation
        let msg = '';
        try { const e = await r.json(); msg = e.description || e.message || ''; } catch (_) {}
        throw new Error(msg || ('HTTP ' + r.status));
      } catch (err) {
        console.warn('cart add error', err);
        showCartError('We couldn’t lock your build just now — please try again.');
        if (lockBtn) {
          lockBtn.disabled = false;
          lockBtn.removeAttribute('aria-disabled');
          lockBtn.textContent = lockLabel || 'Lock my build';
        }
        _cartBusy = false; // P1-26: allow retry after a failure
      }
    }

    async function loadCatalogThenWire() {
      // 2E: show loading state while catalog fetches
      const loadingEl = root.querySelector('[data-lt7-cfg-loading]');
      if (loadingEl) loadingEl.removeAttribute('hidden');
      if (catalogUrl) {
        try {
          const r = await fetch(catalogUrl, { credentials: 'same-origin' });
          if (r.ok) {
            const data = await r.json();
            if (data && data.makes) {
              CATALOG = {
                makes: data.makes,
                trims: data.trims || {},
                default_fitment: data.default_fitment || {},
              };
            }
          }
        } catch (e) {
          console.warn('lt7: catalog load failed, using fallback', e);
        }
      }
      if (loadingEl) loadingEl.setAttribute('hidden', '');
      wire();
    }

    function rebindLayout() { applyLayoutMetrics(root); }
    window.addEventListener('resize', rebindLayout);
    let lastScrollMeasure = 0;
    window.addEventListener('scroll', () => {
      const now = Date.now();
      if (now - lastScrollMeasure < 200) return;
      lastScrollMeasure = now;
      window.requestAnimationFrame(rebindLayout);
    }, { passive: true });

    lockBodyScroll();
    rebindLayout();
    loadCatalogThenWire();
    setTimeout(rebindLayout, 400);
    setTimeout(rebindLayout, 1200);

    root.setAttribute('data-lt7-cfg-ready', 'true');

    window.addEventListener('beforeunload', unlockBodyScroll);
    window.addEventListener('pagehide', unlockBodyScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initConfigurator, { once: true });
  } else {
    initConfigurator();
  }
})();
